/**
 * The page as a list of sections, for the Outline panel: what each section is
 * (preset, custom widget and its version, pasted AI section), in page order.
 */

type Component = {
  _id?: string;
  widgetId: string;
  presetId?: string | null;
  props?: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  subcomponents?: Component[];
};
type Slot = { slotId: string; components: Component[] };
type Preset = { _id: string | { toString(): string }; title: string };
type Schemas = Record<string, { title?: string } | undefined>;

export type OutlineItem = {
  id: string;
  slotId: string;
  index: number;
  title: string;
  tag: 'Preset' | 'Custom' | 'AI' | null;
  /** e.g. "latest", "v1.0.5", "draft" for custom widgets. */
  detail: string | null;
  /** Sections inside a container widget. */
  children: number;
};

export type OutlineGroup = { slotId: string; label: string; items: OutlineItem[] };

export const SLOT_LABELS: Record<string, string> = {
  'menu-bar': 'Header',
  content: 'Body',
  footer: 'Footer',
  // Areas of the FutureSight article and press room templates.
  'left-col': 'Main column',
  'right-col': 'Side column',
  'header-right': 'Header (right)',
  'top-section': 'Top section',
  'additional-content': 'More content'
};
// Top to bottom as the templates draw them.
const SLOT_ORDER = ['menu-bar', 'header-right', 'top-section', 'content', 'left-col', 'right-col', 'additional-content', 'footer'];

const MAX_HEADING = 60;

/** A long heading ends at a whole word with "…", so a shortened name doesn't read as the whole one. */
const shortenName = (text: string, max = MAX_HEADING) => {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max / 2 ? cut.slice(0, space) : cut).replace(/[\s,.;:\-–—]+$/, '')}…`;
};

const headingOf = (html: string) => {
  const m = (html || '').match(/<h[1-4][^>]*>([\s\S]*?)<\/h[1-4]>/i);
  return m ? shortenName(m[1].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()) : '';
};

export const describeComponent = (component: Component, presets: Preset[], schemas: Schemas): Pick<OutlineItem, 'title' | 'tag' | 'detail'> => {
  if (component.presetId) {
    const preset = presets.find(p => String(p._id) === String(component.presetId));
    if (preset) return { title: preset.title, tag: 'Preset', detail: null };
  }
  const props = component.props || {};
  if (component.widgetId === 'CustomWidget' && props.customWidgetName) {
    const v = props.customWidgetVersion;
    return { title: props.customWidgetName, tag: 'Custom', detail: !v ? 'latest' : v === 'draft' ? 'draft' : `v${v}` };
  }
  if (component.widgetId === 'AiGeneratedWidget') {
    return { title: headingOf(props.content && props.content.html) || 'AI section', tag: 'AI', detail: null };
  }
  const schema = schemas[component.widgetId];
  return { title: (schema && schema.title) || component.widgetId, tag: null, detail: null };
};

export const outlineFor = (slots: Slot[], presets: Preset[] = [], schemas: Schemas = {}): OutlineGroup[] => {
  const ordered = [...(slots || [])].sort((a, b) => {
    const ia = SLOT_ORDER.indexOf(a.slotId);
    const ib = SLOT_ORDER.indexOf(b.slotId);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || (a.slotId < b.slotId ? -1 : 1);
  });
  return ordered.map(slot => ({
    slotId: slot.slotId,
    label: SLOT_LABELS[slot.slotId] || slot.slotId,
    items: (slot.components || []).map((component, index) => ({
      id: component._id || `${slot.slotId}-${index}`,
      slotId: slot.slotId,
      index,
      ...describeComponent(component, presets, schemas),
      children: (component.subcomponents || []).length
    }))
  }));
};
